<script setup lang="ts">
import axios from 'axios'
import { VDateInput } from 'vuetify/labs/VDateInput'
import { notifyError } from '@/utils/helpers'
import type { ECOption } from '@/plugins/echarts'



const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const tbAuthority = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        void loadTbAuthority()
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

// ThingsBoard n'autorise l'écriture d'entité qu'aux administrateurs de tenant : sans ce test,
// les utilisateurs client verraient un bouton « Renommer » qui échoue systématiquement en 403.
async function loadTbAuthority() {
    if (!tbToken.value || tbAuthority.value) return
    try {
        const resp = await axios.get(`${TB_BASE_URL}/api/auth/user`, { headers: tbHeaders(tbToken.value) })
        tbAuthority.value = resp.data?.authority ?? null
    } catch {
        tbAuthority.value = null
    }
}

const peutRenommer = computed(() => tbAuthority.value === 'TENANT_ADMIN')
const peutModifierAlarmes = computed(
    () => tbAuthority.value === 'TENANT_ADMIN' || tbAuthority.value === 'CUSTOMER_USER',
)

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    deviceProfileId: { id: string; entityType: string }
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

declare interface TbPageData<T> {
    data: T[]
    totalPages: number
    totalElements: number
    hasNext: boolean
}

declare interface TbRelation {
    from: { entityType: string; id: string }
    to: { entityType: string; id: string }
    type: string
}

declare interface TbAsset {
    id: { entityType: string; id: string }
    name: string
    label: string
    type: string
}

declare interface AssetGroup {
    id: string
    name: string
    label: string
    type: string
    devices: TbDevice[]
}

// Les valeurs existantes sont hétérogènes (hysteresis parfois texte « 0,5 », bornes absentes) :
// tout est optionnel et les clés inconnues sont préservées telles quelles à l'enregistrement.
declare interface AlarmMinMaxAttr {
    enabled?: boolean
    min?: number | string | null
    max?: number | string | null
    hysteresis?: number | string | null
    severity?: string
    sendSMS?: boolean
    sendPushover?: boolean
    [key: string]: unknown
}

declare interface AlarmInactivityAttr {
    enabled?: boolean
    sendSMS?: boolean
    sendPushover?: boolean
    [key: string]: unknown
}

declare interface DeviceAttributes {
    alarmTempMinMax: AlarmMinMaxAttr | null
    alarmHumidityMinMax: AlarmMinMaxAttr | null
    alarmInactivity: AlarmInactivityAttr | null
    inactivityTimeout: number | null
    alarmActiveCount: number | null
    active: boolean | null
    lastActivityTime: number | null
    smsPhoneNumbers: string | null
    application_name: string | null
    dev_eui: string | null
}

declare interface TbAlarm {
    id: { entityType: string; id: string }
    type: string
    name: string
    severity: 'CRITICAL' | 'MAJOR' | 'MINOR' | 'WARNING' | 'INDETERMINATE'
    status: string
    acknowledged: boolean
    cleared: boolean
    startTs: number
    endTs: number | null
    clearTs: number | null
}

declare interface S31BTelemetry {
    temperature: number | null
    humidity: number | null
    battery: number | null
    rssi: number | null
    snr: number | null
    ts: number | null
}

// ── State ─────────────────────────────────────────────────────────────────────

const DEVICE_TYPE = 'S31B-LB'

const allDevices = ref<TbDevice[]>([])
const assetGroups = ref<AssetGroup[]>([])
const ungroupedDevices = ref<TbDevice[]>([])
const selectedDevice = ref<TbDevice | null>(null)
const activeId = ref<string | null>(null)
const deviceSearch = ref('')
const openedGroups = ref<string[]>([])

const telemetry = ref<S31BTelemetry>({
    temperature: null,
    humidity: null,
    battery: null,
    rssi: null,
    snr: null,
    ts: null,
})
const isLoadingTelemetry = ref(false)

const emptyAttrs = (): DeviceAttributes => ({
    alarmTempMinMax: null,
    alarmHumidityMinMax: null,
    alarmInactivity: null,
    inactivityTimeout: null,
    alarmActiveCount: null,
    active: null,
    lastActivityTime: null,
    smsPhoneNumbers: null,
    application_name: null,
    dev_eui: null,
})

const attrs = ref<DeviceAttributes>(emptyAttrs())
const isLoadingAttributes = ref(false)
const alarms = ref<TbAlarm[]>([])
const isLoadingAlarms = ref(false)

// ── Filtered tree ─────────────────────────────────────────────────────────────

const filteredGroups = computed<AssetGroup[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return assetGroups.value
    return assetGroups.value
        .map(g => ({
            ...g,
            devices: g.devices.filter(d =>
                (d.label || d.name).toLowerCase().includes(q) ||
                d.name.toLowerCase().includes(q),
            ),
        }))
        .filter(g => g.devices.length > 0 || g.name.toLowerCase().includes(q) || (g.label || '').toLowerCase().includes(q))
})

const filteredUngrouped = computed<TbDevice[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return ungroupedDevices.value
    return ungroupedDevices.value.filter(d =>
        (d.label || d.name).toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q),
    )
})

// ── Duration helper ───────────────────────────────────────────────────────────

// Certains capteurs stockent leurs seuils en texte, parfois avec une virgule décimale.
function toNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    const parsed = parseFloat(String(value).replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : null
}

function formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ${minutes % 60} min`
    const days = Math.floor(hours / 24)
    return `${days} j ${hours % 24} h`
}

const timeSinceLastTelemetry = computed(() =>
    telemetry.value.ts ? formatDuration(Date.now() - telemetry.value.ts) : null,
)

// ── Telemetry fetch ───────────────────────────────────────────────────────────

async function loadTelemetry(deviceId: string) {
    isLoadingTelemetry.value = true
    telemetry.value = { temperature: null, humidity: null, battery: null, rssi: null, snr: null, ts: null }
    try {
        const token = await getTbToken()
        if (!token) return
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_TempC_SHT,data_Hum_SHT,data_BatV,rssi,snr`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        const d = resp.data
        telemetry.value = {
            temperature: d?.data_TempC_SHT?.[0]?.value !== undefined ? parseFloat(d.data_TempC_SHT[0].value) : null,
            humidity: d?.data_Hum_SHT?.[0]?.value !== undefined ? parseFloat(d.data_Hum_SHT[0].value) : null,
            battery: d?.data_BatV?.[0]?.value !== undefined ? parseFloat(d.data_BatV[0].value) : null,
            rssi: d?.rssi?.[0]?.value !== undefined ? parseFloat(d.rssi[0].value) : null,
            snr: d?.snr?.[0]?.value !== undefined ? parseFloat(d.snr[0].value) : null,
            ts: d?.data_TempC_SHT?.[0]?.ts ?? d?.data_Hum_SHT?.[0]?.ts ?? null,
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingTelemetry.value = false
    }
}

// ── Chart state ──────────────────────────────────────────────────────────────

const today = new Date().toISOString().slice(0, 10)
const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10)

const startDate = ref(sevenDaysAgo)
const endDate = ref(today)
const isLoadingChart = ref(false)

function isoJour(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// `v-date-input` en mode plage prend/rend un tableau ; le calendrier ne colore que les jours
// réellement présents, il faut donc lister chaque jour entre les deux bornes.
function plageDepuisBornes(min: string, max: string): Date[] {
    if (!min || !max) return []
    const dates: Date[] = []
    const jour = new Date(`${min}T00:00:00`)
    const fin = new Date(`${max}T00:00:00`)
    while (jour <= fin) {
        dates.push(new Date(jour))
        jour.setDate(jour.getDate() + 1)
    }
    return dates
}

const plageHistorique = computed<Date[]>({
    get: () => plageDepuisBornes(startDate.value, endDate.value),
    set: (dates) => {
        if (dates.length === 0) return
        startDate.value = isoJour(dates[0])
        endDate.value = isoJour(dates[dates.length - 1])
    },
})

function appliquerPériode(jours: number) {
    const fin = new Date()
    const début = new Date()
    début.setDate(début.getDate() - (jours - 1))
    startDate.value = isoJour(début)
    endDate.value = isoJour(fin)
    loadChart()
}

declare interface ChartPoint { ts: number; value: number }

const tempPoints = ref<ChartPoint[]>([])
const humPoints = ref<ChartPoint[]>([])

declare interface Seuils { min: number | null; max: number | null }

function seuilsActifs(attr: AlarmMinMaxAttr | null): Seuils | null {
    if (!attr?.enabled) return null
    const min = toNumber(attr.min)
    const max = toNumber(attr.max)
    return min === null && max === null ? null : { min, max }
}

const seuilsTemp = computed(() => seuilsActifs(attrs.value.alarmTempMinMax))
const seuilsHum = computed(() => seuilsActifs(attrs.value.alarmHumidityMinMax))

const COULEUR_TEMP = '#2196F3'
const COULEUR_HUM = '#26C281'
const COULEUR_ALARME = '#EF4444'

function markLineSeuils(seuils: Seuils | null, unit: string, couleur: string) {
    if (!seuils) return undefined
    const ligne = (valeur: number, texte: string) => ({
        yAxis: valeur,
        name: texte,
        lineStyle: { color: couleur, type: 'dashed' as const, width: 1.5, opacity: 0.9 },
    })
    const data = []
    if (seuils.min !== null) data.push(ligne(seuils.min, `Min ${seuils.min} ${unit}`))
    if (seuils.max !== null) data.push(ligne(seuils.max, `Max ${seuils.max} ${unit}`))
    return {
        silent: true,
        symbol: ['none', 'none'] as [string, string],
        data,
        label: {
            show: true,
            formatter: (p: { name: string }) => p.name,
            position: 'insideEndTop' as const,
            color: couleur,
            fontSize: 10,
            fontWeight: 'bold' as const,
        },
    }
}

const estSombre = computed(() => theme.global.name.value === 'dark')
const couleurTexte = computed(() => (estSombre.value ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'))
const couleurGrille = computed(() => (estSombre.value ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'))

// ECharts accepte la forme littérale d'un dégradé : pas besoin d'importer `graphic.LinearGradient`.
function dégradé(couleur: string, opacité: number) {
    const alpha = Math.round(opacité * 255).toString(16).padStart(2, '0')
    return {
        type: 'linear' as const,
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
            { offset: 0, color: `${couleur}${alpha}` },
            { offset: 1, color: `${couleur}00` },
        ],
    }
}

const chartOption = computed<ECOption>(() => {
    if (tempPoints.value.length === 0 && humPoints.value.length === 0) return {}
    const axeCommun = {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: couleurTexte.value, fontSize: 10 },
        nameTextStyle: { color: couleurTexte.value, fontSize: 10 },
    }
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line', lineStyle: { color: couleurTexte.value, type: 'dashed' } },
            backgroundColor: estSombre.value ? 'rgba(30,30,30,0.94)' : 'rgba(255,255,255,0.96)',
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: estSombre.value ? '#fff' : '#000', fontSize: 12 },
            formatter(params: unknown) {
                const items = params as Array<{ seriesName: string; value: [number, number]; marker: string }>
                if (!items.length) return ''
                const ts = items[0].value[0]
                const date = new Date(ts).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                let html = `<div style="opacity:.7;margin-bottom:4px;">${date}</div>`
                for (const p of items) {
                    const isHum = p.seriesName === 'Humidité'
                    const unit = isHum ? ' %' : ' °C'
                    html += `${p.marker}${p.seriesName} <b style="float:right;margin-left:16px;">${p.value[1] !== null ? p.value[1].toFixed(1) : '—'}${unit}</b><br/>`
                }
                return html
            },
        },
        legend: {
            top: 0,
            right: 8,
            icon: 'roundRect',
            itemWidth: 14,
            itemHeight: 4,
            textStyle: { color: couleurTexte.value, fontSize: 11 },
        },
        grid: { top: 34, left: 8, right: 8, bottom: 4, containLabel: true },
        xAxis: {
            type: 'time',
            ...axeCommun,
            splitLine: { show: true, lineStyle: { color: couleurGrille.value, type: 'dashed' } },
        },
        yAxis: [
            {
                type: 'value',
                name: '°C',
                position: 'left',
                scale: true,
                ...axeCommun,
                splitLine: { show: true, lineStyle: { color: couleurGrille.value } },
            },
            {
                type: 'value',
                name: '%',
                position: 'right',
                min: 0,
                max: 100,
                ...axeCommun,
                splitLine: { show: false },
            },
        ],
        dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'shift', moveOnMouseWheel: false }],
        series: [
            {
                name: 'Température',
                type: 'line',
                yAxisIndex: 0,
                showSymbol: false,
                smooth: 0.2,
                data: tempPoints.value.map(p => [p.ts, p.value]),
                itemStyle: { color: COULEUR_TEMP },
                lineStyle: { width: 2, color: COULEUR_TEMP },
                areaStyle: { color: dégradé(COULEUR_TEMP, 0.28) },
                markLine: markLineSeuils(seuilsTemp.value, '°C', COULEUR_ALARME),
            },
            {
                name: 'Humidité',
                type: 'line',
                yAxisIndex: 1,
                showSymbol: false,
                smooth: 0.2,
                data: humPoints.value.map(p => [p.ts, p.value]),
                itemStyle: { color: COULEUR_HUM },
                lineStyle: { width: 1.5, color: COULEUR_HUM, type: 'dashed' },
                markLine: markLineSeuils(seuilsHum.value, '%', COULEUR_HUM),
            },
        ],
    }
})

async function loadChart() {
    if (!selectedDevice.value) return
    isLoadingChart.value = true
    tempPoints.value = []
    humPoints.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const startTs = new Date(startDate.value).getTime()
        const endTs = new Date(endDate.value).getTime() + 86400000
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${selectedDevice.value.id.id}/values/timeseries` +
            `?keys=data_TempC_SHT,data_Hum_SHT&startTs=${startTs}&endTs=${endTs}&limit=50000&orderBy=ASC`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        tempPoints.value = (resp.data?.data_TempC_SHT ?? []).map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
        humPoints.value = (resp.data?.data_Hum_SHT ?? []).map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingChart.value = false
    }
}

// ── Diagnostics chart dialog ─────────────────────────────────────────────────

type DiagMetric = 'battery' | 'rssi' | 'snr'

const diagDialog = ref(false)
const diagMetric = ref<DiagMetric>('battery')
const diagPoints = ref<ChartPoint[]>([])
const diagStartDate = ref('')
const diagEndDate = ref('')
const isLoadingDiag = ref(false)

const diagMeta = computed(() => {
    switch (diagMetric.value) {
        case 'battery': return { key: 'data_BatV', label: 'Tension batterie', unit: 'V', color: '#4CAF50', icon: 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 11.5H9V14.5H15V11.5Z' }
        case 'rssi':    return { key: 'rssi',      label: 'RSSI',             unit: 'dBm', color: '#2196F3', icon: 'M19.5,5.5V18.5H17.5V5.5H19.5M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z' }
        case 'snr':     return { key: 'snr',       label: 'SNR',              unit: 'dB',  color: '#9C27B0', icon: 'M16.5,21C13.5,21 12.31,16.76 11.05,12.28C10.14,9.04 9,5 7.5,5C4.11,5 4,11.93 4,12H2C2,11.63 2.06,3 7.5,3C10.5,3 11.71,7.25 12.97,11.74C13.83,14.8 15,19 16.5,19C19.94,19 20.03,12.07 20.03,12H22.03C22.03,12.37 21.97,21 16.5,21Z' }
    }
})

const diagChartOption = computed<ECOption>(() => {
    if (!diagPoints.value.length) return {}
    const meta = diagMeta.value
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            formatter(params: unknown) {
                const items = params as Array<{ value: [number, number]; marker: string }>
                if (!items.length) return ''
                const date = new Date(items[0].value[0]).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                return `<b>${date}</b><br/>${items[0].marker}${meta.label} : <b>${items[0].value[1].toFixed(2)} ${meta.unit}</b>`
            },
        },
        grid: { top: 24, left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'time' },
        yAxis: { type: 'value', name: meta.unit, splitLine: { show: true } },
        series: [{
            name: meta.label,
            type: 'line',
            showSymbol: false,
            data: diagPoints.value.map(p => [p.ts, p.value]),
            lineStyle: { width: 2, color: meta.color },
            itemStyle: { color: meta.color },
            areaStyle: { opacity: 0.08, color: meta.color },
        }],
    }
})

async function loadDiagChart() {
    if (!selectedDevice.value) return
    isLoadingDiag.value = true
    diagPoints.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const startTs = new Date(diagStartDate.value).getTime()
        const endTs = new Date(diagEndDate.value).getTime() + 86400000
        const key = diagMeta.value.key
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${selectedDevice.value.id.id}/values/timeseries` +
            `?keys=${key}&startTs=${startTs}&endTs=${endTs}&limit=50000&orderBy=ASC`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        diagPoints.value = (resp.data?.[key] ?? []).map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingDiag.value = false
    }
}

function openDiagChart(metric: DiagMetric) {
    diagMetric.value = metric
    diagStartDate.value = new Date(Date.now() - 31 * 86400000).toISOString().slice(0, 10)
    diagEndDate.value = new Date().toISOString().slice(0, 10)
    diagDialog.value = true
    loadDiagChart()
}

async function loadAttributes(deviceId: string) {
    isLoadingAttributes.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        const resp = await axios.get(
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/attributes`,
            { headers: tbHeaders(token) },
        )
        const raw = resp.data as Array<{ key: string; value: unknown }>
        const get = (key: string) => raw.find(r => r.key === key)?.value
        attrs.value = {
            alarmTempMinMax: (get('alarmTempMinMax') as AlarmMinMaxAttr) ?? null,
            alarmHumidityMinMax: (get('alarmHumidityMinMax') as AlarmMinMaxAttr) ?? null,
            alarmInactivity: (get('alarmInactivity') as AlarmInactivityAttr) ?? null,
            inactivityTimeout: toNumber(get('inactivityTimeout')),
            alarmActiveCount: (get('alarmActiveCount') as number) ?? null,
            active: (get('active') as boolean) ?? null,
            lastActivityTime: (get('lastActivityTime') as number) ?? null,
            smsPhoneNumbers: (get('smsPhoneNumbers') as string) ?? null,
            application_name: (get('application_name') as string) ?? null,
            dev_eui: (get('dev_eui') as string) ?? null,
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingAttributes.value = false
    }
}

async function loadAlarms(deviceId: string) {
    isLoadingAlarms.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        const resp = await axios.get(
            `${TB_BASE_URL}/api/alarm/DEVICE/${deviceId}?pageSize=20&page=0`,
            { headers: tbHeaders(token) },
        )
        alarms.value = resp.data?.data ?? []
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingAlarms.value = false
    }
}

// ── Alarm helpers ─────────────────────────────────────────────────────────────

const activeAlarmsCount = computed(() => alarms.value.filter(a => !a.cleared).length)

function alarmSeverityColor(severity: string): string {
    switch (severity) {
        case 'CRITICAL': return 'red-darken-4'
        case 'MAJOR': return 'error'
        case 'MINOR': return 'warning'
        case 'WARNING': return 'orange'
        default: return 'secondary'
    }
}

function alarmStatusColor(status: string): string {
    return status.startsWith('ACTIVE') ? 'error' : 'success'
}

function alarmStatusLabel(status: string): string {
    switch (status) {
        case 'ACTIVE_UNACK': return 'Active'
        case 'ACTIVE_ACK': return 'Active (ack.)'
        case 'CLEARED_UNACK': return 'Résolue'
        case 'CLEARED_ACK': return 'Résolue (ack.)'
        default: return status
    }
}

// ── Device selection ──────────────────────────────────────────────────────────

function onSelectDevice(device: TbDevice) {
    activeId.value = device.id.id
    selectedDevice.value = device
    pickerOpen.value = false
    mobileTab.value = 'mesures'
    tempPoints.value = []
    humPoints.value = []
    alarms.value = []
    attrs.value = emptyAttrs()
    loadTelemetry(device.id.id)
    loadChart()
    loadAttributes(device.id.id)
    loadAlarms(device.id.id)
}

// ── Mobile navigation ─────────────────────────────────────────────────────────

type MobileTab = 'mesures' | 'alertes'

const mobileTab = ref<MobileTab>('mesures')
const pickerOpen = ref(false)

declare interface PickerRow {
    kind: 'header' | 'device'
    key: string
    title: string
    device?: TbDevice
}

// Le sélecteur mobile est une liste plate : les groupes deviennent de simples en-têtes,
// ce qui évite d'avoir à replier/déplier des sections sur un petit écran.
const pickerRows = computed<PickerRow[]>(() => {
    const rows: PickerRow[] = []
    for (const group of filteredGroups.value) {
        rows.push({ kind: 'header', key: `g-${group.id}`, title: group.label || group.name })
        for (const device of group.devices)
            rows.push({ kind: 'device', key: device.id.id, title: device.label || device.name, device })
    }
    if (filteredUngrouped.value.length > 0) {
        if (filteredGroups.value.length > 0)
            rows.push({ kind: 'header', key: 'g-none', title: 'Sans groupe' })
        for (const device of filteredUngrouped.value)
            rows.push({ kind: 'device', key: device.id.id, title: device.label || device.name, device })
    }
    return rows
})


// ── Load devices + build tree ─────────────────────────────────────────────────

async function fetchAllDevices(token: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[]= []
    while (true) {
        const url =
            `${TB_BASE_URL}/api/tenant/devices` +
            `?pageSize=${PAGE_SIZE}&page=${page}&type=${encodeURIComponent(DEVICE_TYPE)}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }
    return result.sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
}

async function fetchParentAsset(token: string, deviceId: string): Promise<TbAsset | null> {
    try {
        const rels = await axios.get<TbRelation[]>(
            `${TB_BASE_URL}/api/relations?toId=${deviceId}&toType=DEVICE`,
            { headers: tbHeaders(token) },
        )
        const assetRel = rels.data.find(r => r.from.entityType === 'ASSET')
        if (!assetRel) return null
        const asset = await axios.get<TbAsset>(
            `${TB_BASE_URL}/api/asset/${assetRel.from.id}`,
            { headers: tbHeaders(token) },
        )
        return asset.data
    } catch {
        return null
    }
}

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    selectedDevice.value = null
    activeId.value = null
    assetGroups.value = []
    ungroupedDevices.value = []
    openedGroups.value = []

    try {
        const token = await getTbToken()
        if (!token) return

        const devices = await fetchAllDevices(token)
        allDevices.value = devices

        // Fetch parent assets in parallel (batches of 10 to avoid flooding)
        const BATCH = 10
        const assetMap = new Map<string, TbAsset>()  // assetId -> asset
        const deviceAssetMap = new Map<string, string>() // deviceId -> assetId

        for (let i = 0; i < devices.length; i += BATCH) {
            const batch = devices.slice(i, i + BATCH)
            const results = await Promise.all(batch.map(d => fetchParentAsset(token, d.id.id)))
            results.forEach((asset, idx) => {
                if (asset) {
                    assetMap.set(asset.id.id, asset)
                    deviceAssetMap.set(batch[idx].id.id, asset.id.id)
                }
            })
        }

        // Build grouped structure
        const groupMap = new Map<string, AssetGroup>()
        const ungroup: TbDevice[] = []

        for (const device of devices) {
            const assetId = deviceAssetMap.get(device.id.id)
            if (assetId) {
                const asset = assetMap.get(assetId)!
                if (!groupMap.has(assetId)) {
                    groupMap.set(assetId, {
                        id: assetId,
                        name: asset.name,
                        label: asset.label,
                        type: asset.type,
                        devices: [],
                    })
                }
                groupMap.get(assetId)!.devices.push(device)
            } else {
                ungroup.push(device)
            }
        }

        assetGroups.value = Array.from(groupMap.values())
            .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
        ungroupedDevices.value = ungroup

        // Open all groups by default
        openedGroups.value = assetGroups.value.map(g => g.id)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Renommer un capteur ───────────────────────────────────────────────────────

const renameDialog = ref(false)
const renameLabel = ref('')
const isSavingLabel = ref(false)

function openRenameDialog() {
    if (!selectedDevice.value) return
    renameLabel.value = selectedDevice.value.label || ''
    renameDialog.value = true
}

async function saveLabel() {
    const device = selectedDevice.value
    if (!device) return
    const label = renameLabel.value.trim()
    isSavingLabel.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        // ThingsBoard remplace l'entité complète : on repart de la version courante du serveur
        // pour ne pas écraser les champs modifiés ailleurs entre-temps.
        const current = await axios.get<TbDevice>(
            `${TB_BASE_URL}/api/device/${device.id.id}`,
            { headers: tbHeaders(token) },
        )
        const resp = await axios.post<TbDevice>(
            `${TB_BASE_URL}/api/device`,
            { ...current.data, label },
            { headers: tbHeaders(token) },
        )
        device.label = resp.data.label
        renameDialog.value = false
        Notify.success('Nom du capteur modifié avec succès')
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingLabel.value = false
    }
}

// ── Réglages d'alarme ─────────────────────────────────────────────────────────

declare interface AlarmMinMaxForm {
    enabled: boolean
    min: number | null
    max: number | null
    hysteresis: number | null
    sendSMS: boolean
    sendPushover: boolean
}

declare interface AlarmForm {
    temp: AlarmMinMaxForm
    hum: AlarmMinMaxForm
    inactivityEnabled: boolean
    inactivitySendSMS: boolean
    inactivitySendPushover: boolean
    inactivityTimeoutMin: number | null
}

const alarmDialog = ref(false)
const isSavingAlarms = ref(false)

function toMinMaxForm(attr: AlarmMinMaxAttr | null): AlarmMinMaxForm {
    return {
        enabled: attr?.enabled ?? false,
        min: toNumber(attr?.min),
        max: toNumber(attr?.max),
        hysteresis: toNumber(attr?.hysteresis),
        // Côté règle ThingsBoard, sendSMS absent équivaut à « oui » sauf sévérité MINOR.
        sendSMS: attr?.sendSMS ?? (attr?.severity !== 'MINOR'),
        sendPushover: attr?.sendPushover ?? false,
    }
}

const alarmForm = ref<AlarmForm>({
    temp: toMinMaxForm(null),
    hum: toMinMaxForm(null),
    inactivityEnabled: false,
    inactivitySendSMS: true,
    inactivitySendPushover: false,
    inactivityTimeoutMin: null,
})

function openAlarmDialog() {
    const inactivity = attrs.value.alarmInactivity
    alarmForm.value = {
        temp: toMinMaxForm(attrs.value.alarmTempMinMax),
        hum: toMinMaxForm(attrs.value.alarmHumidityMinMax),
        inactivityEnabled: inactivity?.enabled ?? false,
        inactivitySendSMS: inactivity?.sendSMS ?? true,
        inactivitySendPushover: inactivity?.sendPushover ?? false,
        inactivityTimeoutMin: attrs.value.inactivityTimeout !== null
            ? Math.round(attrs.value.inactivityTimeout / 60000)
            : null,
    }
    alarmDialog.value = true
}

const alarmFormErreur = computed<string | null>(() => {
    const f = alarmForm.value
    if (f.temp.enabled && f.temp.min === null && f.temp.max === null)
        return 'Précisez au moins une borne de température.'
    if (f.hum.enabled && f.hum.min === null && f.hum.max === null)
        return 'Précisez au moins une borne d\'humidité.'
    if (f.temp.min !== null && f.temp.max !== null && f.temp.min >= f.temp.max)
        return 'Le minimum de température doit être inférieur au maximum.'
    if (f.hum.min !== null && f.hum.max !== null && f.hum.min >= f.hum.max)
        return 'Le minimum d\'humidité doit être inférieur au maximum.'
    if (f.inactivityEnabled && (f.inactivityTimeoutMin ?? 0) <= 0)
        return 'Le délai d\'inactivité doit être supérieur à 0 minute.'
    return null
})

// Les clés non exposées (severity, messages personnalisés…) sont conservées telles quelles.
function buildMinMaxAttr(previous: AlarmMinMaxAttr | null, form: AlarmMinMaxForm): AlarmMinMaxAttr {
    return {
        ...(previous ?? {}),
        enabled: form.enabled,
        min: form.min,
        max: form.max,
        hysteresis: form.hysteresis,
        sendSMS: form.sendSMS,
        sendPushover: form.sendPushover,
    }
}

async function saveAlarms() {
    const device = selectedDevice.value
    if (!device || alarmFormErreur.value) return
    isSavingAlarms.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        const f = alarmForm.value
        await axios.post(
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${device.id.id}/attributes/SHARED_SCOPE`,
            {
                alarmTempMinMax: buildMinMaxAttr(attrs.value.alarmTempMinMax, f.temp),
                alarmHumidityMinMax: buildMinMaxAttr(attrs.value.alarmHumidityMinMax, f.hum),
                alarmInactivity: {
                    ...(attrs.value.alarmInactivity ?? {}),
                    enabled: f.inactivityEnabled,
                    sendSMS: f.inactivitySendSMS,
                    sendPushover: f.inactivitySendPushover,
                },
            },
            { headers: tbHeaders(token) },
        )

        const timeoutMs = f.inactivityTimeoutMin !== null ? f.inactivityTimeoutMin * 60000 : null
        if (timeoutMs !== null && timeoutMs !== attrs.value.inactivityTimeout) {
            await axios.post(
                `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${device.id.id}/attributes/SERVER_SCOPE`,
                { inactivityTimeout: timeoutMs },
                { headers: tbHeaders(token) },
            )
        }

        alarmDialog.value = false
        Notify.success('Réglages d\'alarme enregistrés avec succès')
        await loadAttributes(device.id.id)
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingAlarms.value = false
    }
}


// ── Display colors ────────────────────────────────────────────────────────────

const tempColor = computed(() => {
    const t = telemetry.value.temperature
    if (t === null) return undefined
    if (t <= 0) return 'rgb(var(--v-theme-info))'
    if (t <= 10) return 'rgb(var(--v-theme-primary))'
    if (t <= 25) return 'rgb(var(--v-theme-success))'
    if (t <= 35) return 'rgb(var(--v-theme-warning))'
    return 'rgb(var(--v-theme-error))'
})

const humidityColor = computed(() => {
    const h = telemetry.value.humidity
    if (h === null) return undefined
    if (h < 30) return 'rgb(var(--v-theme-warning))'
    if (h <= 70) return 'rgb(var(--v-theme-success))'
    return 'rgb(var(--v-theme-info))'
})
</script>

<template>
    <v-card class="page-card" :loading="isLoading">

        <!-- Page header -->
        <!-- Sur mobile il n'y a pas de v-app-bar : on décale à droite pour libérer le hamburger flottant. -->
        <div class="page-header" :class="smAndDown ? 'ps-16 pe-3 py-2' : 'px-5 pt-5 pb-3'">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px; min-width: 0;">
                    <v-avatar v-if="!smAndDown" color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="26" />
                    </v-avatar>
                    <div v-if="!smAndDown">
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Température & Humidité</div>
                        <div class="text-caption text-medium-emphasis">Capteurs S31B-LB</div>
                    </div>

                    <!-- Sélecteur de capteur (mobile) -->
                    <v-chip
                        v-else
                        color="primary"
                        variant="tonal"
                        size="large"
                        class="culture-chip chip-capteur"
                        prepend-icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z"
                        append-icon="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                        style="cursor: pointer; font-weight: 600;"
                        @click="pickerOpen = true"
                    >
                        {{ selectedDevice ? (selectedDevice.label || selectedDevice.name) : 'Choisir un capteur' }}
                    </v-chip>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        v-if="smAndDown"
                        icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                        color="primary"
                        variant="tonal"
                        size="small"
                        :loading="isLoading"
                        @click="loadDevices"
                    />
                    <v-btn
                        v-else
                        color="primary" variant="tonal" rounded="pill" prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                        :loading="isLoading" @click="loadDevices"
                    >
                        Rafraîchir
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Error banner -->
        <v-alert
            v-if="loginError"
            type="error"
            variant="tonal"
            density="compact"
            class="mx-4 mt-3 mb-0 flex-shrink-0"
            rounded="lg"
        >
            {{ loginError }}
        </v-alert>

        <!-- Main content: tree + detail panel -->
        <div class="d-flex" style="flex: 1 1 auto; overflow: hidden; min-height: 0;">

            <!-- Left: device tree (desktop) -->
            <div v-if="!smAndDown" class="tree-panel d-flex flex-column">

                <!-- Search -->
                <div class="pa-3">
                    <v-text-field
                        v-model="deviceSearch"
                        label="Rechercher un capteur"
                        density="compact"
                        variant="outlined"
                        rounded="lg"
                        hide-details
                        clearable
                        prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                    />
                </div>

                <v-divider />

                <!-- Tree -->
                <div style="flex: 1 1 auto; overflow-y: auto;">
                    <v-list
                        density="compact"
                        nav
                        open-strategy="multiple"
                        :opened="openedGroups"
                        @update:opened="openedGroups = $event"
                    >
                        <!-- Asset groups -->
                        <v-list-group
                            v-for="group in filteredGroups"
                            :key="group.id"
                            :value="group.id"
                        >
                            <template #activator="{ props }">
                                <v-list-item
                                    v-bind="props"
                                    density="compact"
                                    rounded="lg"
                                >
                                    <template #prepend>
                                        <v-icon icon="M6 19H8V21H6V19M12 3L2 8V21H4V13H20V21H22V8L12 3M8 11H4V9H8V11M14 11H10V9H14V11M20 11H16V9H20V11M6 15H8V17H6V15M10 15H12V17H10V15M10 19H12V21H10V19M14 19H16V21H14V19Z" size="16" color="secondary" class="mr-2" />
                                    </template>
                                    <v-list-item-title class="text-caption font-weight-bold">
                                        {{ group.label || group.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle v-if="group.label" class="text-caption">
                                        {{ group.name }}
                                    </v-list-item-subtitle>
                                    <template #append>
                                        <v-chip size="x-small" variant="tonal" color="secondary" class="ml-1">
                                            {{ group.devices.length }}
                                        </v-chip>
                                    </template>
                                </v-list-item>
                            </template>

                            <v-list-item
                                v-for="device in group.devices"
                                :key="device.id.id"
                                density="compact"
                                rounded="lg"
                                :active="activeId === device.id.id"
                                color="primary"
                                class="pl-6"
                                @click="onSelectDevice(device)"
                            >
                                <template #prepend>
                                    <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="14" color="primary" class="mr-2" />
                                </template>
                                <v-list-item-title class="text-caption font-weight-medium">
                                    {{ device.label || device.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle v-if="device.label" class="text-caption">
                                    {{ device.name }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list-group>

                        <!-- Ungrouped devices -->
                        <template v-if="filteredUngrouped.length > 0">
                            <v-list-subheader v-if="filteredGroups.length > 0" class="text-caption">
                                Sans groupe
                            </v-list-subheader>
                            <v-list-item
                                v-for="device in filteredUngrouped"
                                :key="device.id.id"
                                density="compact"
                                rounded="lg"
                                :active="activeId === device.id.id"
                                color="primary"
                                @click="onSelectDevice(device)"
                            >
                                <template #prepend>
                                    <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="14" color="primary" class="mr-2" />
                                </template>
                                <v-list-item-title class="text-caption font-weight-medium">
                                    {{ device.label || device.name }}
                                </v-list-item-title>
                                <v-list-item-subtitle v-if="device.label" class="text-caption">
                                    {{ device.name }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </template>
                    </v-list>

                    <div
                        v-if="!isLoading && filteredGroups.length === 0 && filteredUngrouped.length === 0 && !loginError"
                        class="pa-4 text-center text-medium-emphasis text-caption"
                    >
                        Aucun capteur trouvé
                    </div>
                </div>
            </div>

            <!-- Right: device detail -->
            <div class="detail-panel">
                <!-- Placeholder -->
                <template v-if="!selectedDevice">
                    <div
                        class="d-flex flex-column align-center justify-center text-medium-emphasis pa-4"
                        style="height: 100%; gap: 12px;"
                    >
                        <v-icon icon="M10,9A1,1 0 0,1 11,8A1,1 0 0,1 12,9V13.47L13.21,13.6L18.15,15.79C18.68,16.03 19,16.56 19,17.14V21.5C18.97,22.32 18.32,22.97 17.5,23H11C10.62,23 10.26,22.85 10,22.57L5.1,18.37L5.84,17.6C6.03,17.39 6.3,17.28 6.58,17.28H6.8L10,19V9M11,5A4,4 0 0,1 15,9C15,10.5 14.2,11.77 13,12.46V11.24C13.61,10.69 14,9.89 14,9A3,3 0 0,0 11,6A3,3 0 0,0 8,9C8,9.89 8.39,10.69 9,11.24V12.46C7.8,11.77 7,10.5 7,9A4,4 0 0,1 11,5Z" size="48" />
                        <span class="text-body-2 text-center">
                            {{ smAndDown ? 'Touchez « Choisir un capteur » ci-dessus' : 'Sélectionnez un capteur dans la liste' }}
                        </span>
                        <span v-if="!isLoading && allDevices.length === 0 && !loginError" class="text-body-2 text-center">
                            Contactez-nous pour avoir ces capteurs sur votre ferme !
                            <a href="https://www.agrimetrie.com/capteur-de-temperature-et-dhumidite" target="_blank" rel="noopener noreferrer">
                                Cliquez ici pour plus d'informations.
                            </a>
                        </span>
                    </div>
                </template>

                <!-- Detail -->
                <template v-else>
                    <!-- Onglets (mobile) : évite un défilement interminable sur petit écran -->
                    <!-- flex-grow-0 : `.v-slide-group` est en `flex: 1 0 auto` et s'étirerait sur toute la hauteur. -->
                    <v-tabs
                        v-if="smAndDown"
                        v-model="mobileTab"
                        color="primary"
                        density="compact"
                        grow
                        class="flex-grow-0 flex-shrink-0"
                    >
                        <v-tab value="mesures" prepend-icon="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12C20,14.4 19,16.5 17.3,18C15.9,16.7 14,16 12,16C10,16 8.2,16.7 6.7,18C5,16.5 4,14.4 4,12A8,8 0 0,1 12,4M14,5.89C13.62,5.9 13.26,6.15 13.1,6.54L11.81,9.77L11.71,10C11,10.13 10.41,10.6 10.14,11.26C9.73,12.29 10.23,13.45 11.26,13.86C12.29,14.27 13.45,13.77 13.86,12.74C14.12,12.08 14,11.32 13.57,10.76L13.67,10.5L14.96,7.29L14.97,7.26C15.17,6.75 14.92,6.17 14.41,5.96C14.28,5.91 14.15,5.89 14,5.89M10,6A1,1 0 0,0 9,7A1,1 0 0,0 10,8A1,1 0 0,0 11,7A1,1 0 0,0 10,6M7,9A1,1 0 0,0 6,10A1,1 0 0,0 7,11A1,1 0 0,0 8,10A1,1 0 0,0 7,9M17,9A1,1 0 0,0 16,10A1,1 0 0,0 17,11A1,1 0 0,0 18,10A1,1 0 0,0 17,9Z" text="Mesures" />
                        <v-tab value="alertes" prepend-icon="M10 21H14C14 22.1 13.1 23 12 23S10 22.1 10 21M21 19V20H3V19L5 17V11C5 7.9 7 5.2 10 4.3V4C10 2.9 10.9 2 12 2S14 2.9 14 4V4.3C17 5.2 19 7.9 19 11V17L21 19M17 11C17 8.2 14.8 6 12 6S7 8.2 7 11V18H17V11Z" text="Alertes" />
                    </v-tabs>

                    <div class="detail-scroll" :class="{ 'detail-scroll-mobile': smAndDown }">

                    <!-- Device header card -->
                    <v-card
                        v-show="!smAndDown || mobileTab === 'mesures'"
                        variant="outlined" rounded="lg" class="flex-shrink-0"
                    >
                        <v-card-title class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
                                <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="20" />
                            </v-avatar>
                            <div style="flex: 1 1 140px; min-width: 0;">
                                <div class="text-subtitle-1 font-weight-bold text-truncate">
                                    {{ selectedDevice.label || selectedDevice.name }}
                                </div>
                                <div v-if="selectedDevice.label" class="text-caption text-medium-emphasis text-truncate">
                                    {{ selectedDevice.name }}
                                </div>
                                <div v-if="attrs.application_name" class="text-caption text-medium-emphasis text-truncate" style="font-style: italic;">
                                    {{ attrs.application_name }}
                                </div>
                            </div>
                            <!-- Diagnostics chips -->
                            <div v-if="!isLoadingTelemetry" class="d-flex align-center flex-wrap" style="gap: 6px;">
                                <v-chip
                                    v-if="attrs.alarmActiveCount !== null && attrs.alarmActiveCount > 0"
                                    size="x-small"
                                    color="error"
                                    variant="flat"
                                    prepend-icon="M21,19V20H3V19L5,17V11C5,7.9 7.03,5.17 10,4.29C10,4.19 10,4.1 10,4A2,2 0 0,1 12,2A2,2 0 0,1 14,4C14,4.1 14,4.19 14,4.29C16.97,5.17 19,7.9 19,11V17L21,19M14,21A2,2 0 0,1 12,23A2,2 0 0,1 10,21M19.75,3.19L18.33,4.61C20.04,6.3 21,8.6 21,11H23C23,8.07 21.84,5.25 19.75,3.19M1,11H3C3,8.6 3.96,6.3 5.67,4.61L4.25,3.19C2.16,5.25 1,8.07 1,11Z"
                                >
                                    {{ attrs.alarmActiveCount }} alarme{{ attrs.alarmActiveCount > 1 ? 's' : '' }}
                                </v-chip>
                                <v-chip
                                    v-if="telemetry.battery !== null"
                                    size="x-small"
                                    :color="telemetry.battery >= 3.5 ? 'success' : telemetry.battery >= 3.2 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="telemetry.battery >= 3.5 ? 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 7H9V10H15V7M15 11.5H9V14.5H15V11.5Z' : telemetry.battery >= 3.2 ? 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 11.5H9V14.5H15V11.5Z' : 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16'"
                                    @click="openDiagChart('battery')"
                                >
                                    {{ telemetry.battery.toFixed(2) }} V
                                </v-chip>
                                <v-chip
                                    v-if="telemetry.rssi !== null"
                                    size="x-small"
                                    :color="telemetry.rssi >= -70 ? 'success' : telemetry.rssi >= -85 ? 'warning' : 'error'"
                                    variant="tonal"
                                    :prepend-icon="telemetry.rssi >= -70 ? 'M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z' : telemetry.rssi >= -85 ? 'M19.5,5.5V18.5H17.5V5.5H19.5M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z' : 'M19.5,5.5V18.5H17.5V5.5H19.5M12.5,10.5V18.5H10.5V10.5H12.5M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z'"
                                    @click="openDiagChart('rssi')"
                                >
                                    {{ telemetry.rssi }} dBm
                                </v-chip>
                                <v-chip
                                    v-if="telemetry.snr !== null"
                                    size="x-small"
                                    color="secondary"
                                    variant="tonal"
                                    prepend-icon="M16.5,21C13.5,21 12.31,16.76 11.05,12.28C10.14,9.04 9,5 7.5,5C4.11,5 4,11.93 4,12H2C2,11.63 2.06,3 7.5,3C10.5,3 11.71,7.25 12.97,11.74C13.83,14.8 15,19 16.5,19C19.94,19 20.03,12.07 20.03,12H22.03C22.03,12.37 21.97,21 16.5,21Z"
                                    @click="openDiagChart('snr')"
                                >
                                    {{ telemetry.snr.toFixed(1) }} dB
                                </v-chip>
                            </div>
                            <v-progress-circular v-else size="16" width="2" indeterminate color="primary" />

                            <!-- Actions d'édition -->
                            <v-menu v-if="peutRenommer || peutModifierAlarmes">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" icon="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z" variant="text" size="small" />
                                </template>
                                <v-list density="compact">
                                    <v-list-item
                                        v-if="peutRenommer"
                                        prepend-icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"
                                        title="Renommer le capteur"
                                        @click="openRenameDialog"
                                    />
                                    <v-list-item
                                        v-if="peutModifierAlarmes"
                                        prepend-icon="M22.72 19.5C22.74 19.33 22.75 19.17 22.75 19S22.74 18.67 22.72 18.5L23.77 17.68C23.87 17.61 23.89 17.5 23.83 17.36L22.83 15.64C22.77 15.53 22.64 15.5 22.53 15.53L21.28 16C21 15.83 20.75 15.66 20.44 15.54L20.25 14.21C20.23 14.09 20.13 14 20 14H18C17.88 14 17.77 14.09 17.75 14.21L17.57 15.54C17.25 15.66 17 15.83 16.72 16L15.5 15.53C15.37 15.5 15.23 15.53 15.17 15.64L14.17 17.36C14.11 17.5 14.14 17.61 14.23 17.68L15.29 18.5C15.27 18.67 15.25 18.84 15.25 19S15.27 19.33 15.29 19.5L14.23 20.32C14.14 20.39 14.11 20.53 14.17 20.64L15.17 22.37C15.23 22.5 15.37 22.5 15.5 22.5L16.72 21.97C17 22.17 17.25 22.34 17.57 22.47L17.75 23.79C17.77 23.91 17.88 24 18 24H20C20.13 24 20.23 23.91 20.25 23.79L20.44 22.47C20.75 22.34 21 22.17 21.28 21.97L22.53 22.5C22.64 22.5 22.77 22.5 22.83 22.37L23.83 20.64C23.89 20.53 23.87 20.39 23.77 20.32L22.72 19.5M19 20.75C18.04 20.75 17.25 19.97 17.25 19S18.04 17.25 19 17.25 20.75 18.03 20.75 19 19.97 20.75 19 20.75M12.08 20H3V19L5 17V11C5 7.9 7 5.2 10 4.3V4C10 2.9 10.9 2 12 2S14 2.9 14 4V4.3C17 5.2 19 7.9 19 11V12C18.31 12 17.63 12.11 17 12.29V11C17 8.2 14.8 6 12 6S7 8.2 7 11V18H12.08C12.03 18.33 12 18.66 12 19C12 19.34 12.03 19.67 12.08 20M12.3 21C12.5 21.6 12.74 22.17 13.06 22.69C12.75 22.88 12.39 23 12 23C10.9 23 10 22.1 10 21H12.3Z"
                                        title="Réglages d'alarme"
                                        :disabled="isLoadingAttributes"
                                        @click="openAlarmDialog"
                                    />
                                </v-list>
                            </v-menu>
                        </v-card-title>

                        <v-divider />

                        <v-card-text class="pa-3">
                            <!-- Type chip -->
                            <div class="mb-3">
                                <v-chip size="small" color="primary" variant="tonal" prepend-icon="M21.41 11.58L12.41 2.58A2 2 0 0 0 11 2H4A2 2 0 0 0 2 4V11A2 2 0 0 0 2.59 12.42L11.59 21.42A2 2 0 0 0 13 22A2 2 0 0 0 14.41 21.41L21.41 14.41A2 2 0 0 0 22 13A2 2 0 0 0 21.41 11.58M13 20L4 11V4H11L20 13M6.5 5A1.5 1.5 0 1 1 5 6.5A1.5 1.5 0 0 1 6.5 5Z">
                                    {{ selectedDevice.type || '—' }}
                                </v-chip>
                            </div>

                            <!-- Latest telemetry -->
                            <div v-if="isLoadingTelemetry" class="d-flex align-center" style="gap: 8px;">
                                <v-progress-circular size="16" width="2" indeterminate color="primary" />
                                <span class="text-caption text-medium-emphasis">Chargement...</span>
                            </div>

                            <div v-else class="d-flex flex-wrap align-start" style="gap: 16px;">

                                <!-- Temperature -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Température</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span :style="{ color: tempColor }">
                                            {{ telemetry.temperature !== null ? telemetry.temperature.toFixed(1) : '—' }}
                                        </span>
                                        <span v-if="telemetry.temperature !== null" class="text-body-2 text-medium-emphasis pb-1">°C</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Humidity -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Humidité relative</div>
                                    <div class="text-h5 font-weight-bold d-flex align-end" style="gap: 4px; line-height: 1.1;">
                                        <span :style="{ color: humidityColor }">
                                            {{ telemetry.humidity !== null ? telemetry.humidity.toFixed(1) : '—' }}
                                        </span>
                                        <span v-if="telemetry.humidity !== null" class="text-body-2 text-medium-emphasis pb-1">%</span>
                                    </div>
                                </div>

                                <v-divider vertical style="height: 48px; align-self: center;" />

                                <!-- Last seen -->
                                <div class="sensor-stat">
                                    <div class="text-caption text-medium-emphasis">Dernière donnée</div>
                                    <div class="text-body-2 font-weight-bold">
                                        {{ timeSinceLastTelemetry ?? '—' }}
                                    </div>
                                    <div v-if="telemetry.ts" class="text-caption text-medium-emphasis">
                                        {{ new Date(telemetry.ts).toLocaleString('fr-CA') }}
                                    </div>
                                </div>
                            </div>

                            <!-- Alarm thresholds -->
                            <template v-if="!isLoadingAttributes && (attrs.alarmTempMinMax || attrs.alarmHumidityMinMax)">
                                <v-divider class="my-3" />
                                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                                    <span class="text-caption text-medium-emphasis mr-1">
                                        <v-icon size="12" icon="M10 21H14C14 22.1 13.1 23 12 23S10 22.1 10 21M21 19V20H3V19L5 17V11C5 7.9 7 5.2 10 4.3V4C10 2.9 10.9 2 12 2S14 2.9 14 4V4.3C17 5.2 19 7.9 19 11V17L21 19M17 11C17 8.2 14.8 6 12 6S7 8.2 7 11V18H17V11Z" class="mr-1" />Seuils :
                                    </span>
                                    <template v-if="attrs.alarmTempMinMax">
                                        <v-chip v-if="toNumber(attrs.alarmTempMinMax.min) !== null" size="x-small" :color="attrs.alarmTempMinMax.enabled ? 'info' : 'default'" variant="tonal" prepend-icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V12H11V5A1 1 0 0 1 12 4Z">
                                            {{ toNumber(attrs.alarmTempMinMax.min) }} °C
                                        </v-chip>
                                        <v-chip v-if="toNumber(attrs.alarmTempMinMax.max) !== null" size="x-small" :color="attrs.alarmTempMinMax.enabled ? 'error' : 'default'" variant="tonal" prepend-icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5H11A1 1 0 0 1 12 4Z">
                                            {{ toNumber(attrs.alarmTempMinMax.max) }} °C
                                        </v-chip>
                                    </template>
                                    <v-divider v-if="attrs.alarmTempMinMax && attrs.alarmHumidityMinMax" vertical style="height: 16px;" />
                                    <template v-if="attrs.alarmHumidityMinMax">
                                        <v-chip v-if="toNumber(attrs.alarmHumidityMinMax.min) !== null" size="x-small" :color="attrs.alarmHumidityMinMax.enabled ? 'warning' : 'default'" variant="tonal" prepend-icon="M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z">
                                            {{ toNumber(attrs.alarmHumidityMinMax.min) }} %
                                        </v-chip>
                                        <v-chip v-if="toNumber(attrs.alarmHumidityMinMax.max) !== null" size="x-small" :color="attrs.alarmHumidityMinMax.enabled ? 'info' : 'default'" variant="tonal" prepend-icon="M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z">
                                            {{ toNumber(attrs.alarmHumidityMinMax.max) }} %
                                        </v-chip>
                                    </template>
                                    <v-chip v-if="attrs.alarmInactivity?.enabled" size="x-small" color="warning" variant="tonal" prepend-icon="M23,12H17V10L20.39,6H17V4H23V6L19.62,10H23V12M15,16H9V14L12.39,10H9V8H15V10L11.62,14H15V16M7,20H1V18L4.39,14H1V12H7V14L3.62,18H7V20Z">
                                        Inactivité
                                    </v-chip>
                                </div>
                            </template>
                        </v-card-text>
                    </v-card>

                    <!-- Historical chart -->
                    <v-card
                        v-show="!smAndDown || mobileTab === 'mesures'"
                        variant="outlined" rounded="lg"
                        :class="smAndDown ? 'carte-remplissage' : 'mt-4'"
                    >
                        <v-card-title class="py-2 px-4">
                            <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                                <div v-if="!smAndDown" class="d-flex align-center" style="gap: 8px; flex: 0 0 auto;">
                                    <v-icon icon="M16,11.78L20.24,4.45L21.97,5.45L16.74,14.5L10.23,10.75L5.46,19H22V21H2V3H4V17.54L9.5,8L16,11.78Z" size="18" />
                                    <span class="text-subtitle-2">Historique</span>
                                </div>
                                <v-date-input
                                    v-model="plageHistorique"
                                    label="Période"
                                    multiple="range"
                                    variant="outlined"
                                    density="compact"
                                    rounded="lg"
                                    hide-details
                                    autocomplete="off"
                                    prepend-icon=""
                                    prepend-inner-icon="M7 11H9V13H7V11M21 5V19C21 20.11 20.11 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.9 3 5 3H6V1H8V3H16V1H18V3H19C20.11 3 21 3.9 21 5M5 7H19V5H5V7M19 19V9H5V19H19M15 13H17V11H15V13M11 13H13V11H11V13Z"
                                    :picker-props="{ color: 'primary' }"
                                    style="min-width: 190px; flex: 1 1 190px; max-width: 300px;"
                                    @update:model-value="loadChart()"
                                />
                                <div class="d-flex align-center" style="gap: 4px;">
                                    <v-btn size="x-small" variant="tonal" rounded="pill" @click="appliquerPériode(7)">7 j</v-btn>
                                    <v-btn size="x-small" variant="tonal" rounded="pill" @click="appliquerPériode(30)">30 j</v-btn>
                                </div>
                                <v-spacer v-if="!smAndDown" />
                                <v-btn
                                    icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                                    variant="text"
                                    size="small"
                                    :loading="isLoadingChart"
                                    @click="loadChart"
                                />
                            </div>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingChart" class="d-flex align-center justify-center chart-zone">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <div
                                v-else-if="tempPoints.length === 0 && humPoints.length === 0"
                                class="d-flex align-center justify-center text-medium-emphasis text-caption chart-zone"
                            >
                                Aucune donnée sur la période sélectionnée
                            </div>
                            <VChart
                                v-else
                                :option="chartOption"
                                autoresize
                                class="chart-zone"
                                style="width: 100%;"
                            />
                        </v-card-text>
                    </v-card>

                    <!-- Alarm history -->
                    <v-card
                        v-show="!smAndDown || mobileTab === 'alertes'"
                        variant="outlined" rounded="lg"
                        :class="smAndDown ? '' : 'mt-4'"
                    >
                        <v-card-title class="py-2 px-4">
                            <div class="d-flex align-center" style="gap: 8px;">
                                <v-icon icon="M10 21H14C14 22.1 13.1 23 12 23S10 22.1 10 21M21 19V20H3V19L5 17V11C5 7.9 7 5.2 10 4.3V4C10 2.9 10.9 2 12 2S14 2.9 14 4V4.3C17 5.2 19 7.9 19 11V17L21 19M17 11C17 8.2 14.8 6 12 6S7 8.2 7 11V18H17V11Z" size="18" />
                                <span class="text-subtitle-2">Alertes récentes</span>
                                <v-chip v-if="activeAlarmsCount > 0" size="x-small" color="error" variant="flat" class="ml-1">
                                    {{ activeAlarmsCount }} active{{ activeAlarmsCount > 1 ? 's' : '' }}
                                </v-chip>
                                <v-spacer />
                                <span class="text-caption text-medium-emphasis">20 dernières</span>
                            </div>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-0">
                            <div v-if="isLoadingAlarms" class="d-flex align-center justify-center pa-6">
                                <v-progress-circular size="20" indeterminate color="primary" />
                            </div>
                            <div v-else-if="alarms.length === 0" class="pa-4 text-center text-caption text-medium-emphasis">
                                Aucune alerte enregistrée
                            </div>
                            <v-list v-else density="compact" class="pa-0">
                                <template v-for="(alarm, idx) in alarms" :key="idx">
                                    <v-list-item density="compact" class="px-4 py-2">
                                        <template #prepend>
                                            <v-icon :color="alarmSeverityColor(alarm.severity)" size="16" icon="M21,19V20H3V19L5,17V11C5,7.9 7.03,5.17 10,4.29C10,4.19 10,4.1 10,4A2,2 0 0,1 12,2A2,2 0 0,1 14,4C14,4.1 14,4.19 14,4.29C16.97,5.17 19,7.9 19,11V17L21,19M14,21A2,2 0 0,1 12,23A2,2 0 0,1 10,21" class="mr-2" />
                                        </template>
                                        <v-list-item-title class="text-caption font-weight-medium">
                                            {{ alarm.name || alarm.type }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle class="text-caption">
                                            {{ new Date(alarm.startTs).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}
                                            <template v-if="alarm.clearTs">
                                                &rarr; {{ new Date(alarm.clearTs).toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }}
                                            </template>
                                        </v-list-item-subtitle>
                                        <!-- Sur mobile les puces passent sous le texte plutôt que d'écraser le titre -->
                                        <div v-if="smAndDown" class="d-flex align-center flex-wrap mt-1" style="gap: 4px;">
                                            <v-chip size="x-small" :color="alarmSeverityColor(alarm.severity)" variant="tonal">
                                                {{ alarm.severity }}
                                            </v-chip>
                                            <v-chip size="x-small" :color="alarmStatusColor(alarm.status)" variant="tonal">
                                                {{ alarmStatusLabel(alarm.status) }}
                                            </v-chip>
                                        </div>
                                        <template #append>
                                            <div v-if="!smAndDown" class="d-flex align-center" style="gap: 4px;">
                                                <v-chip size="x-small" :color="alarmSeverityColor(alarm.severity)" variant="tonal">
                                                    {{ alarm.severity }}
                                                </v-chip>
                                                <v-chip size="x-small" :color="alarmStatusColor(alarm.status)" variant="tonal">
                                                    {{ alarmStatusLabel(alarm.status) }}
                                                </v-chip>
                                            </div>
                                        </template>
                                    </v-list-item>
                                    <v-divider v-if="idx < alarms.length - 1" />
                                </template>
                            </v-list>
                        </v-card-text>
                    </v-card>
                    </div>
                </template>
            </div>
        </div>
    </v-card>

    <!-- Sélecteur de capteur (mobile) -->
    <v-dialog v-model="pickerOpen" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card>
            <v-card-title class="d-flex align-center pt-4 px-4" style="gap: 10px;">
                <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Capteurs</span>
                <v-spacer />
                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="small" @click="pickerOpen = false" />
            </v-card-title>
            <div class="px-4 pb-3">
                <v-text-field
                    v-model="deviceSearch"
                    label="Rechercher un capteur"
                    density="compact"
                    variant="outlined"
                    rounded="lg"
                    hide-details
                    clearable
                    autofocus
                    prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                />
            </div>
            <v-divider />
            <v-card-text class="pa-0">
                <v-list density="comfortable" nav>
                    <template v-for="row in pickerRows" :key="row.key">
                        <v-list-subheader v-if="row.kind === 'header'" class="text-caption font-weight-bold">
                            {{ row.title }}
                        </v-list-subheader>
                        <v-list-item
                            v-else
                            rounded="lg"
                            :active="activeId === row.key"
                            color="primary"
                            @click="onSelectDevice(row.device!)"
                        >
                            <template #prepend>
                                <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="18" color="primary" class="mr-2" />
                            </template>
                            <v-list-item-title class="text-body-2 font-weight-medium">
                                {{ row.title }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                                {{ row.device!.name }}
                            </v-list-item-subtitle>
                        </v-list-item>
                    </template>
                </v-list>
                <div v-if="pickerRows.length === 0" class="pa-6 text-center text-medium-emphasis text-caption">
                    Aucun capteur trouvé
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>

    <!-- Renommer un capteur -->
    <v-dialog v-model="renameDialog" max-width="480" :fullscreen="smAndDown">
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Renommer le capteur</span>
                <v-spacer />
                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="small" @click="renameDialog = false" />
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
                <v-text-field
                    v-model="renameLabel"
                    label="Nom affiché"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    autofocus
                    counter="255"
                    :hint="`Identifiant technique : ${selectedDevice?.name ?? ''}`"
                    persistent-hint
                    @keyup.enter="saveLabel"
                />
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="renameDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary" variant="flat" rounded="pill"
                    :loading="isSavingLabel"
                    :disabled="renameLabel.trim().length === 0"
                    @click="saveLabel"
                >
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Réglages d'alarme -->
    <v-dialog v-model="alarmDialog" max-width="620" :fullscreen="smAndDown" scrollable>
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="M22.72 19.5C22.74 19.33 22.75 19.17 22.75 19S22.74 18.67 22.72 18.5L23.77 17.68C23.87 17.61 23.89 17.5 23.83 17.36L22.83 15.64C22.77 15.53 22.64 15.5 22.53 15.53L21.28 16C21 15.83 20.75 15.66 20.44 15.54L20.25 14.21C20.23 14.09 20.13 14 20 14H18C17.88 14 17.77 14.09 17.75 14.21L17.57 15.54C17.25 15.66 17 15.83 16.72 16L15.5 15.53C15.37 15.5 15.23 15.53 15.17 15.64L14.17 17.36C14.11 17.5 14.14 17.61 14.23 17.68L15.29 18.5C15.27 18.67 15.25 18.84 15.25 19S15.27 19.33 15.29 19.5L14.23 20.32C14.14 20.39 14.11 20.53 14.17 20.64L15.17 22.37C15.23 22.5 15.37 22.5 15.5 22.5L16.72 21.97C17 22.17 17.25 22.34 17.57 22.47L17.75 23.79C17.77 23.91 17.88 24 18 24H20C20.13 24 20.23 23.91 20.25 23.79L20.44 22.47C20.75 22.34 21 22.17 21.28 21.97L22.53 22.5C22.64 22.5 22.77 22.5 22.83 22.37L23.83 20.64C23.89 20.53 23.87 20.39 23.77 20.32L22.72 19.5M19 20.75C18.04 20.75 17.25 19.97 17.25 19S18.04 17.25 19 17.25 20.75 18.03 20.75 19 19.97 20.75 19 20.75M12.08 20H3V19L5 17V11C5 7.9 7 5.2 10 4.3V4C10 2.9 10.9 2 12 2S14 2.9 14 4V4.3C17 5.2 19 7.9 19 11V12C18.31 12 17.63 12.11 17 12.29V11C17 8.2 14.8 6 12 6S7 8.2 7 11V18H12.08C12.03 18.33 12 18.66 12 19C12 19.34 12.03 19.67 12.08 20M12.3 21C12.5 21.6 12.74 22.17 13.06 22.69C12.75 22.88 12.39 23 12 23C10.9 23 10 22.1 10 21H12.3Z" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Réglages d'alarme</span>
                <v-spacer />
                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="small" @click="alarmDialog = false" />
            </v-card-title>
            <v-divider />

            <v-card-text class="pt-4">
                <!-- Température -->
                <div class="d-flex align-center mb-2" style="gap: 8px;">
                    <v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="18" color="primary" />
                    <span class="text-subtitle-2">Température</span>
                    <v-spacer />
                    <v-switch
                        v-model="alarmForm.temp.enabled"
                        color="primary" density="compact" hide-details inset
                    />
                </div>
                <v-row dense>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.temp.min"
                            label="Min (°C)" type="number" step="0.1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.temp.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.temp.max"
                            label="Max (°C)" type="number" step="0.1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.temp.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.temp.hysteresis"
                            label="Hystérésis (°C)" type="number" step="0.1" min="0"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            placeholder="1,5" persistent-placeholder
                            :disabled="!alarmForm.temp.enabled"
                        />
                    </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mt-2" style="gap: 16px;">
                    <v-switch
                        v-model="alarmForm.temp.sendSMS" label="SMS"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.temp.enabled"
                    />
                    <v-switch
                        v-model="alarmForm.temp.sendPushover" label="Pushover"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.temp.enabled"
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Humidité -->
                <div class="d-flex align-center mb-2" style="gap: 8px;">
                    <v-icon icon="M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z" size="18" color="primary" />
                    <span class="text-subtitle-2">Humidité relative</span>
                    <v-spacer />
                    <v-switch
                        v-model="alarmForm.hum.enabled"
                        color="primary" density="compact" hide-details inset
                    />
                </div>
                <v-row dense>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.hum.min"
                            label="Min (%)" type="number" step="1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.hum.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.hum.max"
                            label="Max (%)" type="number" step="1"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            :disabled="!alarmForm.hum.enabled"
                        />
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.hum.hysteresis"
                            label="Hystérésis (%)" type="number" step="0.5" min="0"
                            variant="outlined" density="compact" rounded="lg" hide-details clearable
                            placeholder="1,5" persistent-placeholder
                            :disabled="!alarmForm.hum.enabled"
                        />
                    </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mt-2" style="gap: 16px;">
                    <v-switch
                        v-model="alarmForm.hum.sendSMS" label="SMS"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.hum.enabled"
                    />
                    <v-switch
                        v-model="alarmForm.hum.sendPushover" label="Pushover"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.hum.enabled"
                    />
                </div>

                <v-divider class="my-4" />

                <!-- Inactivité -->
                <div class="d-flex align-center mb-2" style="gap: 8px;">
                    <v-icon icon="M23,12H17V10L20.39,6H17V4H23V6L19.62,10H23V12M15,16H9V14L12.39,10H9V8H15V10L11.62,14H15V16M7,20H1V18L4.39,14H1V12H7V14L3.62,18H7V20Z" size="18" color="primary" />
                    <span class="text-subtitle-2">Inactivité</span>
                    <v-spacer />
                    <v-switch
                        v-model="alarmForm.inactivityEnabled"
                        color="primary" density="compact" hide-details inset
                    />
                </div>
                <v-row dense>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model.number="alarmForm.inactivityTimeoutMin"
                            label="Délai (min)" type="number" step="1" min="1"
                            variant="outlined" density="compact" rounded="lg" hide-details
                            :disabled="!alarmForm.inactivityEnabled"
                        />
                    </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mt-2" style="gap: 16px;">
                    <v-switch
                        v-model="alarmForm.inactivitySendSMS" label="SMS"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.inactivityEnabled"
                    />
                    <v-switch
                        v-model="alarmForm.inactivitySendPushover" label="Pushover"
                        color="primary" density="compact" hide-details inset
                        :disabled="!alarmForm.inactivityEnabled"
                    />
                </div>

                <v-alert
                    v-if="alarmFormErreur"
                    type="warning" variant="tonal" density="compact" rounded="lg" class="mt-4"
                >
                    {{ alarmFormErreur }}
                </v-alert>
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-5 py-3">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="alarmDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary" variant="flat" rounded="pill"
                    :loading="isSavingAlarms"
                    :disabled="alarmFormErreur !== null"
                    @click="saveAlarms"
                >
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- Diagnostics chart dialog -->
    <v-dialog v-model="diagDialog" max-width="680" :fullscreen="smAndDown" scrollable>
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon :icon="diagMeta.icon" size="20" :color="diagMeta.color" />
                <span class="text-subtitle-1 font-weight-bold">{{ diagMeta.label }}</span>
                <div v-if="selectedDevice" class="text-caption text-medium-emphasis font-weight-regular">
                    — {{ selectedDevice.label || selectedDevice.name }}
                </div>
                <v-spacer />
                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="small" @click="diagDialog = false" />
            </v-card-title>

            <v-divider />

            <!-- Date range row -->
            <div class="d-flex align-center px-4 pt-3 pb-2 flex-wrap" style="gap: 10px;">
                <v-text-field
                    v-model="diagStartDate"
                    label="Date de début"
                    type="date"
                    variant="outlined"
                    density="compact"
                    rounded="lg"
                    hide-details
                    style="min-width: 150px; flex: 1 1 150px; max-width: 200px;"
                />
                <v-text-field
                    v-model="diagEndDate"
                    label="Date de fin"
                    type="date"
                    variant="outlined"
                    density="compact"
                    rounded="lg"
                    hide-details
                    style="min-width: 150px; flex: 1 1 150px; max-width: 200px;"
                />
                <v-btn
                    color="primary"
                    variant="tonal"
                    rounded="lg"
                    size="small"
                    prepend-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
                    :loading="isLoadingDiag"
                    @click="loadDiagChart"
                >
                    Afficher
                </v-btn>
            </div>

            <v-card-text class="pa-2">
                <div v-if="isLoadingDiag" class="d-flex align-center justify-center" style="height: 280px;">
                    <v-progress-circular indeterminate color="primary" />
                </div>
                <div
                    v-else-if="diagPoints.length === 0"
                    class="d-flex align-center justify-center text-medium-emphasis text-caption"
                    style="height: 280px;"
                >
                    Aucune donnée sur la période sélectionnée
                </div>
                <VChart
                    v-else
                    :option="diagChartOption"
                    autoresize
                    style="height: 280px; width: 100%;"
                />
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
    flex-shrink: 0;
}

/* Comme la page Carte : la carte occupe tout le viewport, sans marge ni coins arrondis. */
.page-card {
    height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    border-radius: 0;
}

.culture-chip {
    transition: opacity 0.15s;
}
.culture-chip:hover {
    opacity: 0.78;
}

/* Le nom du capteur peut être long : la puce se tronque au lieu de pousser le bouton hors écran. */
.chip-capteur {
    max-width: 55vw;
}
.chip-capteur :deep(.v-chip__content) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tree-panel {
    flex-shrink: 0;
    width: 300px;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.detail-panel {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
}

.detail-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    min-height: 0;
}

/* Sur mobile un seul onglet est visible à la fois : il occupe toute la hauteur restante. */
.detail-scroll-mobile {
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 8px;
}

.carte-remplissage {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 260px;
}
.carte-remplissage :deep(.v-card-text) {
    flex: 1 1 auto;
    min-height: 0;
}

.chart-zone {
    height: 260px;
}
.carte-remplissage .chart-zone {
    height: 100%;
}

.sensor-stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
}
</style>
